import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Plus, Save, Trash2, ReceiptText } from "lucide-react";
import { toast } from "sonner";

export default function ReceiptsSettings() {
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => { load(); }, []);

  async function load() {
    try {
      const { data } = await api.get("/receipts/settings");
      setCfg(data);
    } catch (e) {
      toast.error("Could not load receipts settings");
    }
  }

  function setBusiness(patch) {
    setCfg((c) => ({ ...c, business: { ...c.business, ...patch } }));
  }
  function setProvider(idx, patch) {
    setCfg((c) => ({
      ...c,
      providers: c.providers.map((p, i) => (i === idx ? { ...p, ...patch } : p)),
    }));
  }
  function addProvider() {
    setCfg((c) => ({
      ...c,
      providers: [...c.providers, { id: `prov${c.providers.length + 1}`, name: "", title: "Exercise Physiologist", provider_no: "" }],
    }));
  }
  function removeProvider(idx) {
    setCfg((c) => ({ ...c, providers: c.providers.filter((_, i) => i !== idx) }));
  }
  function setItemCode(idx, patch) {
    setCfg((c) => ({
      ...c,
      item_codes: c.item_codes.map((it, i) => (i === idx ? { ...it, ...patch } : it)),
    }));
  }
  function addItemCode() {
    setCfg((c) => ({
      ...c,
      item_codes: [...c.item_codes, { code: "", label: "", description: "" }],
    }));
  }
  function removeItemCode(idx) {
    setCfg((c) => ({ ...c, item_codes: c.item_codes.filter((_, i) => i !== idx) }));
  }
  function setGp(idx, patch) {
    setCfg((c) => ({
      ...c,
      gps: (c.gps || []).map((g, i) => (i === idx ? { ...g, ...patch } : g)),
    }));
  }
  function addGp() {
    setCfg((c) => ({
      ...c,
      gps: [...(c.gps || []), { id: `gp${(c.gps || []).length + 1}`, name: "", provider_no: "", clinic: "" }],
    }));
  }
  function removeGp(idx) {
    setCfg((c) => ({ ...c, gps: (c.gps || []).filter((_, i) => i !== idx) }));
  }

  async function save() {
    setSaving(true);
    try {
      const { data } = await api.post("/receipts/settings", {
        business: cfg.business,
        providers: cfg.providers,
        item_codes: cfg.item_codes,
        gps: cfg.gps || [],
        default_payment_method: cfg.default_payment_method,
      });
      setCfg(data);
      toast.success("Receipts settings saved");
    } catch (e) {
      toast.error("Save failed");
    } finally { setSaving(false); }
  }

  if (!cfg) return null;

  return (
    <Card className="p-6 border-zinc-200" data-testid="receipts-settings">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <ReceiptText className="h-5 w-5" />
          <h2 className="text-lg font-semibold">Tax Receipts</h2>
        </div>
        <Button onClick={save} disabled={saving} data-testid="receipts-settings-save">
          <Save className="h-4 w-4 mr-2" /> Save
        </Button>
      </div>

      <div className="space-y-6">
        <div>
          <h3 className="text-sm font-semibold text-zinc-950 mb-2">Business details (appears in receipt header)</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div><Label>Trading name</Label><Input value={cfg.business.trading_name || ""} onChange={(e) => setBusiness({ trading_name: e.target.value })} data-testid="rc-business-name" /></div>
            <div><Label>ABN</Label><Input value={cfg.business.abn || ""} onChange={(e) => setBusiness({ abn: e.target.value })} data-testid="rc-business-abn" /></div>
            <div><Label>Address line 1</Label><Input value={cfg.business.address_line_1 || ""} onChange={(e) => setBusiness({ address_line_1: e.target.value })} /></div>
            <div><Label>Address line 2</Label><Input value={cfg.business.address_line_2 || ""} onChange={(e) => setBusiness({ address_line_2: e.target.value })} /></div>
            <div><Label>Address line 3 (state)</Label><Input value={cfg.business.address_line_3 || ""} onChange={(e) => setBusiness({ address_line_3: e.target.value })} /></div>
            <div><Label>Footer org name</Label><Input value={cfg.business.footer_org || ""} onChange={(e) => setBusiness({ footer_org: e.target.value })} /></div>
            <div><Label>Default payment method</Label><Input value={cfg.default_payment_method || ""} onChange={(e) => setCfg((c) => ({ ...c, default_payment_method: e.target.value }))} /></div>
          </div>
        </div>

        <div>
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-semibold text-zinc-950">Providers</h3>
            <Button variant="outline" size="sm" onClick={addProvider} data-testid="rc-add-provider"><Plus className="h-4 w-4 mr-1" /> Add</Button>
          </div>
          <div className="space-y-2">
            {cfg.providers.map((p, idx) => (
              <div key={idx} className="grid grid-cols-1 md:grid-cols-12 gap-2 items-end" data-testid={`rc-provider-${idx}`}>
                <div className="md:col-span-2"><Label className="text-xs">Key</Label><Input value={p.id || ""} onChange={(e) => setProvider(idx, { id: e.target.value })} /></div>
                <div className="md:col-span-4"><Label className="text-xs">Name</Label><Input value={p.name || ""} onChange={(e) => setProvider(idx, { name: e.target.value })} /></div>
                <div className="md:col-span-3"><Label className="text-xs">Title</Label><Input value={p.title || ""} onChange={(e) => setProvider(idx, { title: e.target.value })} /></div>
                <div className="md:col-span-2"><Label className="text-xs">Provider No.</Label><Input value={p.provider_no || ""} onChange={(e) => setProvider(idx, { provider_no: e.target.value })} /></div>
                <div className="md:col-span-1"><Button variant="ghost" size="icon" onClick={() => removeProvider(idx)} disabled={cfg.providers.length <= 1}><Trash2 className="h-4 w-4 text-zinc-500" /></Button></div>
              </div>
            ))}
          </div>
        </div>

        <div>
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-semibold text-zinc-950">Item code presets</h3>
            <Button variant="outline" size="sm" onClick={addItemCode} data-testid="rc-add-item-code"><Plus className="h-4 w-4 mr-1" /> Add</Button>
          </div>
          <div className="space-y-2">
            {cfg.item_codes.map((it, idx) => (
              <div key={idx} className="grid grid-cols-1 md:grid-cols-12 gap-2 items-end" data-testid={`rc-item-code-${idx}`}>
                <div className="md:col-span-2"><Label className="text-xs">Code</Label><Input value={it.code || ""} onChange={(e) => setItemCode(idx, { code: e.target.value })} /></div>
                <div className="md:col-span-4"><Label className="text-xs">Label</Label><Input value={it.label || ""} onChange={(e) => setItemCode(idx, { label: e.target.value })} /></div>
                <div className="md:col-span-5"><Label className="text-xs">Description (pre-fills line)</Label><Input value={it.description || ""} onChange={(e) => setItemCode(idx, { description: e.target.value })} /></div>
                <div className="md:col-span-1"><Button variant="ghost" size="icon" onClick={() => removeItemCode(idx)}><Trash2 className="h-4 w-4 text-zinc-500" /></Button></div>
              </div>
            ))}
          </div>
        </div>

        <div>
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-semibold text-zinc-950">Referring GPs</h3>
            <Button variant="outline" size="sm" onClick={addGp} data-testid="rc-add-gp"><Plus className="h-4 w-4 mr-1" /> Add GP</Button>
          </div>
          <p className="text-xs text-zinc-500 mb-2">
            Saved GPs appear as a dropdown on the Receipts page — pick one to auto-fill the "Referring GP" block (name, provider no., clinic) on the receipt. Used for Medicare claiming.
          </p>
          <div className="space-y-2">
            {(cfg.gps || []).length === 0 && (
              <div className="text-xs text-zinc-500 italic py-2">
                No GPs saved yet. Click "Add GP" to add one.
              </div>
            )}
            {(cfg.gps || []).map((g, idx) => (
              <div key={idx} className="grid grid-cols-1 md:grid-cols-12 gap-2 items-end" data-testid={`rc-gp-${idx}`}>
                <div className="md:col-span-2"><Label className="text-xs">Key</Label><Input value={g.id || ""} onChange={(e) => setGp(idx, { id: e.target.value })} placeholder="gp_rachel" /></div>
                <div className="md:col-span-3"><Label className="text-xs">Name</Label><Input value={g.name || ""} onChange={(e) => setGp(idx, { name: e.target.value })} placeholder="Dr Rachel De Jong" data-testid={`rc-gp-name-${idx}`} /></div>
                <div className="md:col-span-2"><Label className="text-xs">Provider No.</Label><Input value={g.provider_no || ""} onChange={(e) => setGp(idx, { provider_no: e.target.value })} placeholder="1864942B" /></div>
                <div className="md:col-span-4"><Label className="text-xs">Clinic</Label><Input value={g.clinic || ""} onChange={(e) => setGp(idx, { clinic: e.target.value })} placeholder="Andrew Place Clinic" /></div>
                <div className="md:col-span-1"><Button variant="ghost" size="icon" onClick={() => removeGp(idx)} data-testid={`rc-gp-remove-${idx}`}><Trash2 className="h-4 w-4 text-zinc-500" /></Button></div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </Card>
  );
}
